import { readCookie, readOrCreateCookie, writeCookie } from './cookies';

/**
 * Identity primitives the SDK owns end-to-end.
 *
 * `ekho_aid` (anonymous ID) — UUID v4 in a 2-year cookie. Same buyer across
 * visits and sessions on this host. Joined to PostHog / Segment / etc. via
 * an explicit alias call, never by sharing their cookies — that decoupling
 * survives analytics-vendor migrations and ad-blocked sessions.
 *
 * `ekho_sid` (session ID) — UUID v4 in a 30-minute sliding-window cookie.
 * Matches GA4 / Segment session semantics: any activity within 30 minutes
 * extends the session; longer gaps mint a new ID. Used to group page views,
 * cart attempts, and chat turns into a single visit.
 *
 * Both values flow through `MarketingAttribution.{anonymousId,sessionId}` so
 * downstream systems (cart, CRM, warehouse) can stitch records back to the
 * anonymous buyer without ever needing PII.
 *
 * Two access modes let consent gate cookie writes without breaking the checkout
 * handoff. `capture*` reads-or-mints and writes the cookie; call it only once
 * tracking is allowed. `resolveIdentity` returns the IDs from an in-memory
 * holder (seeded from the cookie, or minted) and writes nothing, so a
 * pre-consent handoff can forward them while the origin stays cookie-free.
 * `persistIdentity` later writes those same values, so the ID a visitor carries
 * before consent matches the one stored after.
 */

const ANONYMOUS_ID_COOKIE = 'ekho_aid';
const SESSION_ID_COOKIE = 'ekho_sid';
const ANONYMOUS_ID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2; // 2 years
const SESSION_ID_MAX_AGE_SECONDS = 60 * 30; // 30 minutes (sliding)

export interface ResolvedIdentity {
  anonymousId: string;
  sessionId: string;
}

function mintId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Pre-randomUUID browser fallback. Good-enough entropy for an anonymous
  // identifier; collision risk across our scale is negligible.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * In-memory identity, seeded once per page load. A value minted before consent
 * becomes the one `persistIdentity` writes on grant, so identity stays
 * continuous into the checkout handoff.
 */
let memoized: ResolvedIdentity | null = null;

function ensureMemoized(): ResolvedIdentity {
  const cookieAnonymousId = readCookie(ANONYMOUS_ID_COOKIE);
  const cookieSessionId = readCookie(SESSION_ID_COOKIE);
  if (!memoized) {
    memoized = {
      anonymousId: cookieAnonymousId || mintId(),
      sessionId: cookieSessionId || mintId(),
    };
    return memoized;
  }
  // A live cookie wins over the frozen value. `track()` renews and can rotate
  // the 30-minute sliding `ekho_sid` cookie, so re-reading here keeps
  // resolve/persist and the checkout handoff aligned with the id analytics
  // actually uses, instead of overwriting a rotated cookie with a stale
  // in-memory session. Absent a cookie (pre-consent) the minted value stands,
  // so the id forwarded before consent still equals the one persisted on grant.
  if (cookieAnonymousId) memoized.anonymousId = cookieAnonymousId;
  if (cookieSessionId) memoized.sessionId = cookieSessionId;
  return memoized;
}

/** Read or mint the long-lived anonymous ID. Refreshes the 2-year cookie on each call. */
export function captureAnonymousId(): string {
  return readOrCreateCookie(ANONYMOUS_ID_COOKIE, mintId, ANONYMOUS_ID_MAX_AGE_SECONDS);
}

/**
 * Read or mint the session ID. Each call refreshes the 30-minute expiry, so
 * any SDK boot or attribution capture inside that window keeps the same ID;
 * 30+ minutes of inactivity expires the cookie and the next call mints fresh.
 */
export function captureSessionId(): string {
  return readOrCreateCookie(SESSION_ID_COOKIE, mintId, SESSION_ID_MAX_AGE_SECONDS);
}

/**
 * Return the current identity from the in-memory holder, writing no cookie.
 * Safe before consent: the handoff can forward the IDs while the origin stays
 * cookie-free.
 */
export function resolveIdentity(): ResolvedIdentity {
  return { ...ensureMemoized() };
}

/**
 * Write the in-memory identity to the `ekho_aid` / `ekho_sid` cookies,
 * refreshing their windows. Call once tracking is allowed. Writes the same
 * values `resolveIdentity` returns, so identity is stable across consent.
 */
export function persistIdentity(): ResolvedIdentity {
  const ids = ensureMemoized();
  writeCookie(ANONYMOUS_ID_COOKIE, ids.anonymousId, ANONYMOUS_ID_MAX_AGE_SECONDS);
  writeCookie(SESSION_ID_COOKIE, ids.sessionId, SESSION_ID_MAX_AGE_SECONDS);
  return { ...ids };
}

/**
 * Expire the identity cookies and drop the in-memory holder, so nothing
 * survives a revoke.
 */
export function clearIdentity(): void {
  writeCookie(ANONYMOUS_ID_COOKIE, '', 0);
  writeCookie(SESSION_ID_COOKIE, '', 0);
  memoized = null;
}

/** Reset in-memory identity. Exported for testing only. */
export function resetIdentityForTesting(): void {
  memoized = null;
}
